const { stableStringify } = require('./json');
const { resultToMethodsText } = require('./methods');
const { normalizeLanguage } = require('../i18n/translations');

function fallbackHash(input) {
  let hash = 2166136261;

  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

async function createExperimentSignature(result) {
  const payload = {
    seed: result.seed,
    algorithmId: result.algorithmId,
    parameters: result.parameters,
    sequence: result.sequence
  };
  const stablePayload = stableStringify(payload);

  if (typeof crypto !== 'undefined' && crypto.subtle && typeof TextEncoder !== 'undefined') {
    const encoded = new TextEncoder().encode(stablePayload);
    const digest = await crypto.subtle.digest('SHA-256', encoded);
    const bytes = Array.from(new Uint8Array(digest));
    return bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  return fallbackHash(stablePayload);
}

function escapeHtml(value) {
  return String(value === undefined || value === null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

const REPORT_LABELS = {
  en: {
    lang: 'en',
    title: 'randFlow Randomization Report',
    studyFallback: 'Untitled study',
    print: 'Print / Save PDF',
    inputs: 'Study Inputs',
    technique: 'Technique',
    participants: 'Participants',
    arms: 'Arms',
    generatedAt: 'Generated At',
    validation: 'Validation',
    normalized: 'Normalized Parameters',
    parameter: 'Parameter',
    value: 'Value',
    signature: 'Experiment Signature',
    signatureNote: 'This signature is generated from the seed, technique, normalized parameters, and full randomization list.',
    verifyNote: 'To verify a report, regenerate the same technique with the same inputs and seed, then compare this signature with the JSON export signature.',
    audit: 'Audit Trail',
    session: 'Session ID',
    eventCount: 'Event Count',
    chainHead: 'Chain Head',
    compliance: 'Compliance Stage',
    complianceValue: 'V2 static audit preparation',
    auditNote: 'This local audit trail is hash-chained for tamper evidence. It supports regulatory preparation, but does not replace validated Part 11 infrastructure.',
    balance: 'Balance Summary',
    arm: 'Arm',
    count: 'Count',
    percent: 'Percent',
    imbalance: 'Imbalance',
    noWarnings: 'No validation warnings.',
    list: 'Randomization List',
    patient: 'Patient ID',
    order: 'Order',
    block: 'Block',
    cluster: 'Cluster',
    stratum: 'Stratum',
    appendix: 'Methods Appendix',
    footer: 'transparent, reproducible randomization for clinical trials'
  },
  pt: {
    lang: 'pt-BR',
    title: 'Relatório de Randomização randFlow',
    studyFallback: 'Estudo sem título',
    print: 'Imprimir / Salvar PDF',
    inputs: 'Dados do Estudo',
    technique: 'Técnica',
    participants: 'Participantes',
    arms: 'Braços',
    generatedAt: 'Gerado em',
    validation: 'Validação',
    normalized: 'Parâmetros Normalizados',
    parameter: 'Parâmetro',
    value: 'Valor',
    signature: 'Assinatura do Experimento',
    signatureNote: 'Esta assinatura é gerada a partir da seed, técnica, parâmetros normalizados e lista completa de randomização.',
    verifyNote: 'Para verificar um relatório, gere novamente a mesma técnica com os mesmos dados e seed, então compare esta assinatura com a assinatura do export JSON.',
    audit: 'Trilha de Auditoria',
    session: 'ID da sessão',
    eventCount: 'Número de eventos',
    chainHead: 'Chain head',
    compliance: 'Estágio de compliance',
    complianceValue: 'Preparação de auditoria estática V2',
    auditNote: 'Esta trilha local é encadeada por hash para evidência de adulteração. Ela apoia preparação regulatória, mas não substitui infraestrutura validada Part 11.',
    balance: 'Resumo de Balanceamento',
    arm: 'Braço',
    count: 'N',
    percent: 'Percentual',
    imbalance: 'Desequilíbrio',
    noWarnings: 'Nenhum aviso de validação.',
    list: 'Lista de Randomização',
    patient: 'Paciente',
    order: 'Ordem',
    block: 'Bloco',
    cluster: 'Cluster',
    stratum: 'Estrato',
    appendix: 'Apêndice de Métodos',
    footer: 'randomização transparente e reproduzível para ensaios clínicos'
  },
  es: {
    lang: 'es',
    title: 'Informe de Aleatorización randFlow',
    studyFallback: 'Estudio sin título',
    print: 'Imprimir / Guardar PDF',
    inputs: 'Datos del Estudio',
    technique: 'Técnica',
    participants: 'Participantes',
    arms: 'Brazos',
    generatedAt: 'Generado en',
    validation: 'Validación',
    normalized: 'Parámetros Normalizados',
    parameter: 'Parámetro',
    value: 'Valor',
    signature: 'Firma del Experimento',
    signatureNote: 'Esta firma se genera a partir de la seed, técnica, parámetros normalizados y lista completa de aleatorización.',
    verifyNote: 'Para verificar un informe, regenere la misma técnica con los mismos datos y seed, y compare esta firma con la firma del export JSON.',
    audit: 'Trazabilidad de Auditoría',
    session: 'ID de sesión',
    eventCount: 'Número de eventos',
    chainHead: 'Chain head',
    compliance: 'Etapa de compliance',
    complianceValue: 'Preparación de auditoría estática V2',
    auditNote: 'Esta trazabilidad local está encadenada por hash para evidencia de alteración. Apoya la preparación regulatoria, pero no sustituye infraestructura validada Part 11.',
    balance: 'Resumen de Balance',
    arm: 'Brazo',
    count: 'N',
    percent: 'Porcentaje',
    imbalance: 'Desequilibrio',
    noWarnings: 'Sin avisos de validación.',
    list: 'Lista de Aleatorización',
    patient: 'Paciente',
    order: 'Orden',
    block: 'Bloque',
    cluster: 'Cluster',
    stratum: 'Estrato',
    appendix: 'Apéndice de Métodos',
    footer: 'aleatorización transparente y reproducible para ensayos clínicos'
  }
};

function buildReportHtml(result, signature, auditSnapshot = null, options = {}) {
  const language = options.language ? normalizeLanguage(options.language) : 'en';
  const labels = REPORT_LABELS[language];
  const methodologyText = resultToMethodsText(result, signature, { language });
  const getArmLabel = (index) => result.parameters.armLabels[index] || `Arm ${index + 1}`;
  const parameterRows = Object.entries(result.parameters)
    .filter(([key]) => !['technique'].includes(key))
    .map(([key, value]) => `<tr><td>${escapeHtml(key)}</td><td>${escapeHtml(JSON.stringify(value))}</td></tr>`)
    .join('');

  const armRows = result.metrics.armCounts
    .map((count, index) => `<tr><td>${escapeHtml(getArmLabel(index))}</td><td>${count}</td><td>${((count / result.parameters.n) * 100).toFixed(1)}%</td></tr>`)
    .join('');

  const sequenceRows = result.sequence
    .map((patient) => `
      <tr>
        <td>${escapeHtml(patient.patientId)}</td>
        <td>${patient.sequenceOrder}</td>
        <td>${escapeHtml(patient.arm)}</td>
        <td>${escapeHtml(patient.blockNumber || '-')}</td>
        <td>${escapeHtml(patient.cluster || '-')}</td>
        <td>${escapeHtml(patient.stratum || '-')}</td>
      </tr>
    `)
    .join('');

  const warnings = result.metrics.warnings.length
    ? result.metrics.warnings.map((warning) => `<li>${escapeHtml(warning)}</li>`).join('')
    : `<li>${escapeHtml(labels.noWarnings)}</li>`;
  const auditRows = auditSnapshot && auditSnapshot.events.length
    ? auditSnapshot.events.map((event) => `
      <tr>
        <td>${event.index}</td>
        <td>${escapeHtml(event.timestamp)}</td>
        <td>${escapeHtml(event.action)}</td>
        <td><code>${escapeHtml(event.previousHash)}</code></td>
        <td><code>${escapeHtml(event.eventHash)}</code></td>
      </tr>
    `).join('')
    : '';

  return `<!DOCTYPE html>
<html lang="${escapeHtml(labels.lang)}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(labels.title)} - ${escapeHtml(result.parameters.studyName || labels.studyFallback)}</title>
  <style>
    :root { --primary: #15996a; --ink: #17201c; --muted: #5d6b65; --line: #d9e7e0; --soft: #eef8f3; }
    body { color: var(--ink); font-family: Arial, sans-serif; margin: 32px; line-height: 1.45; }
    header { border-bottom: 3px solid var(--primary); margin-bottom: 24px; padding-bottom: 16px; }
    h1 { color: var(--primary); font-size: 28px; margin: 0 0 4px; }
    h2 { border-bottom: 1px solid var(--line); color: var(--ink); font-size: 18px; margin-top: 28px; padding-bottom: 6px; }
    .meta { color: var(--muted); margin: 0; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(2, 1fr); }
    .box { background: var(--soft); border: 1px solid var(--line); border-radius: 6px; padding: 12px; }
    .label { color: var(--muted); display: block; font-size: 12px; text-transform: uppercase; }
    .value { font-weight: 700; word-break: break-word; }
    table { border-collapse: collapse; font-size: 12px; width: 100%; }
    th, td { border: 1px solid var(--line); padding: 7px; text-align: left; }
    th { background: var(--soft); }
    code { background: #f4f4f4; border-radius: 4px; padding: 2px 4px; word-break: break-all; }
    pre { background: #f8fbf9; border: 1px solid var(--line); border-radius: 6px; padding: 12px; white-space: pre-wrap; }
    footer { color: var(--muted); font-size: 11px; margin-top: 28px; }
    @media print {
      body { margin: 18mm; }
      button { display: none; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; page-break-after: auto; }
    }
  </style>
</head>
<body>
  <button onclick="window.print()">${escapeHtml(labels.print)}</button>
  <header>
    <h1>${escapeHtml(labels.title)}</h1>
    <p class="meta">${escapeHtml(result.parameters.studyName || labels.studyFallback)} - ${escapeHtml(result.version)}</p>
  </header>

  <h2>${escapeHtml(labels.inputs)}</h2>
  <div class="grid">
    <div class="box"><span class="label">${escapeHtml(labels.technique)}</span><span class="value">${escapeHtml(result.algorithm)}</span></div>
    <div class="box"><span class="label">Seed</span><span class="value">${escapeHtml(result.seed)}</span></div>
    <div class="box"><span class="label">${escapeHtml(labels.participants)}</span><span class="value">${result.parameters.n}</span></div>
    <div class="box"><span class="label">${escapeHtml(labels.arms)}</span><span class="value">${result.parameters.arms}</span></div>
    <div class="box"><span class="label">${escapeHtml(labels.generatedAt)}</span><span class="value">${escapeHtml(result.timestamp)}</span></div>
    <div class="box"><span class="label">${escapeHtml(labels.validation)}</span><span class="value">${escapeHtml(result.metrics.validation)}</span></div>
  </div>

  <h2>${escapeHtml(labels.normalized)}</h2>
  <table><thead><tr><th>${escapeHtml(labels.parameter)}</th><th>${escapeHtml(labels.value)}</th></tr></thead><tbody>${parameterRows}</tbody></table>

  <h2>${escapeHtml(labels.signature)}</h2>
  <p>${escapeHtml(labels.signatureNote)}</p>
  <p><code>${escapeHtml(signature)}</code></p>
  <p>${escapeHtml(labels.verifyNote)}</p>

  ${auditSnapshot ? `
  <h2>${escapeHtml(labels.audit)}</h2>
  <div class="grid">
    <div class="box"><span class="label">${escapeHtml(labels.session)}</span><span class="value">${escapeHtml(auditSnapshot.sessionId)}</span></div>
    <div class="box"><span class="label">${escapeHtml(labels.eventCount)}</span><span class="value">${auditSnapshot.eventCount}</span></div>
    <div class="box"><span class="label">${escapeHtml(labels.chainHead)}</span><span class="value">${escapeHtml(auditSnapshot.chainHead)}</span></div>
    <div class="box"><span class="label">${escapeHtml(labels.compliance)}</span><span class="value">${escapeHtml(labels.complianceValue)}</span></div>
  </div>
  <table>
    <thead><tr><th>#</th><th>Timestamp</th><th>Action</th><th>Previous Hash</th><th>Event Hash</th></tr></thead>
    <tbody>${auditRows}</tbody>
  </table>
  <p>${escapeHtml(labels.auditNote)}</p>
  ` : ''}

  <h2>${escapeHtml(labels.balance)}</h2>
  <table><thead><tr><th>${escapeHtml(labels.arm)}</th><th>${escapeHtml(labels.count)}</th><th>${escapeHtml(labels.percent)}</th></tr></thead><tbody>${armRows}</tbody></table>
  <p><strong>${escapeHtml(labels.imbalance)}:</strong> ${result.metrics.imbalance} (${result.metrics.imbalancePercent}%)</p>
  <ul>${warnings}</ul>

  <h2>${escapeHtml(labels.list)}</h2>
  <table>
    <thead><tr><th>${escapeHtml(labels.patient)}</th><th>${escapeHtml(labels.order)}</th><th>${escapeHtml(labels.arm)}</th><th>${escapeHtml(labels.block)}</th><th>${escapeHtml(labels.cluster)}</th><th>${escapeHtml(labels.stratum)}</th></tr></thead>
    <tbody>${sequenceRows}</tbody>
  </table>

  <h2>${escapeHtml(labels.appendix)}</h2>
  <pre>${escapeHtml(methodologyText)}</pre>

  <footer>randFlow - ${escapeHtml(labels.footer)} - randflow.com</footer>
</body>
</html>`;
}

module.exports = {
  buildReportHtml,
  createExperimentSignature
};
